import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { RoutesPaths } from "../Router/constant";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { logoutAPI } from "../Services/authService";
import { Messageslist, MessageslistAdmin } from "../Services/messagesService";

const MESSAGE_BADGE_PAGE = 1;
const MESSAGE_BADGE_LIMIT = 1000;
const ADMIN_READ_ROOMS_STORAGE_KEY = "adminMessagesReadRooms";
const PARTNER_READ_ROOMS_STORAGE_KEY = "partnerMessagesReadRooms";
const ADMIN_MESSAGE_TYPES = ["user", "partner"];

function SidebarSkeleton() {
  return (
    <div>
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [IsSignOutModel, setIsSignOutModel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setUserRole] = useState(null);
  const [sideLoader, setSideLoader] = useState(true);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const storedRole = localStorage.getItem("userRole");
      setUserRole(storedRole);
      setSideLoader(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const fetchMessageUnreadCount = useCallback(async () => {
    if (!role) return;

    try {
      const responses =
        role === "admin"
          ? await Promise.all(
              ADMIN_MESSAGE_TYPES.map((type) =>
                MessageslistAdmin(
                  type,
                  MESSAGE_BADGE_PAGE,
                  MESSAGE_BADGE_LIMIT,
                ),
              ),
            )
          : [await Messageslist(MESSAGE_BADGE_PAGE, MESSAGE_BADGE_LIMIT)];

      const storageKey =
        role === "admin"
          ? ADMIN_READ_ROOMS_STORAGE_KEY
          : PARTNER_READ_ROOMS_STORAGE_KEY;
      const readRooms = getReadRooms(storageKey);
      const unreadCount = responses
        .flatMap((response) => getRoomsData(response))
        .reduce(
          (total, room) =>
            total + (isRoomRead(room, readRooms) ? 0 : getUnreadCount(room)),
          0,
        );

      setMessageUnreadCount(unreadCount);
    } catch (error) {
      console.error("Load message unread count failed:", error);
      setMessageUnreadCount(0);
    }
  }, [role]);

  useEffect(() => {
    fetchMessageUnreadCount();
  }, [fetchMessageUnreadCount, location.pathname]);

  useEffect(() => {
    window.addEventListener("messageRoomsRead", fetchMessageUnreadCount);
    window.addEventListener("storage", fetchMessageUnreadCount);

    return () => {
      window.removeEventListener("messageRoomsRead", fetchMessageUnreadCount);
      window.removeEventListener("storage", fetchMessageUnreadCount);
    };
  }, [fetchMessageUnreadCount]);

  const signOutModel = () => setIsSignOutModel(true);
  const signOutModelClose = () => setIsSignOutModel(false);

  const signOutSectionClose = async () => {
    try {
      const res = await logoutAPI();
      if (res.data?.data) {
        toast.success("Logged out successfully");

        localStorage.removeItem("token");
        localStorage.removeItem("userRole");
        navigate(RoutesPaths.SIGNIN);
      } else {
        toast.error(res.data?.message || "Logout failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Logout failed");
    }
  };

  const menuItems = useMemo(() => {
    if (!role) return [];

    return [
      {
        path: RoutesPaths.DASHBOARD,
        label: "Dashboard",
        icon:
          role === "partner"
            ? "/assets/img/dashboardPartner.svg"
            : "/assets/img/dashboard.svg",
      },

      ...(role === "partner"
        ? [
            {
              path: RoutesPaths.FREIGHTAGREEMENT,
              label: "Vendor contract",
              icon: "/assets/img/partnerPartner.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.USERMANAGEMENT,
              label: "User management",
              icon: "/assets/img/document.svg",
            },
          ]
        : []),

      {
        path: RoutesPaths.BOOKINGMANAGEMENT,
        label: "Booking management",
        icon:
          role === "partner"
            ? "/assets/img/office-documentsPartner.svg"
            : "/assets/img/office-documents.svg",
      },
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.SUBSCRIBERMANAGEMENT,
              label: "Newsletter management",
              icon: "/assets/img/subscriber.svg",
            },
          ]
        : []),

      // {
      //   path: RoutesPaths.PAYMENT,
      //   label: "Payment & transactions",
      //   icon:
      //     role === "partner"
      //       ? "/assets/img/rupee-circlePartner.svg"
      //       : "/assets/img/rupee-circle.svg",
      // },

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.CONTACTSUPPORT,
              label: "Contact support",
              icon: "/assets/img/board.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.SURVEY,
              label: "Survey",
              icon: "/assets/img/survey.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.PARTNERS,
              label: "Vendors",
              icon: "/assets/img/partner-side.svg",
            },
          ]
        : []),
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.ADMINMESSAGES,
              label: "Messages",
              icon: "/assets/img/message.svg",
            },
          ]
        : []),

      ...(role === "partner"
        ? [
            {
              path: RoutesPaths.PARTNERMESSAGES,
              label: "Messages",
              icon: "/assets/img/messageWhite.svg",
            },
          ]
        : []),
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.ADMINMANAGEMENT,
              label: "Admin management",
              icon: "/assets/img/partner-side.svg",
            },
          ]
        : []),

      {
        path: RoutesPaths.PROFILE,
        label: "Settings",
        icon:
          role === "partner"
            ? "/assets/img/filterPartner.svg"
            : "/assets/img/filter.svg",
      },
    ];
  }, [role]);

  const filteredItems = menuItems.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <>
      <div>
        <NavLink to={RoutesPaths.DASHBOARD} className="logoSidebar">
          {role === "partner" ? (
            <img
              src="/assets/img/quimeraLogoPartner.svg"
              alt="Logo"
              className=""
            />
          ) : (
            <img src="/assets/img/quimeraLogo.svg" alt="Logo" />
          )}
        </NavLink>

        {/* <div
          className={`searchSideBar ${role === "partner" ? "searchSideBarPartner" : ""
            }`}
        >
          <img
            src={
              role === "partner"
                ? "/assets/img/searchPartner.svg"
                : "/assets/img/search.svg"
            }
            alt="Search"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div> */}

        {/* ✅ SKELETON FIRST */}
        {sideLoader ? (
          <div className="sideBarSectionScroll">
            <SidebarSkeleton />
          </div>
        ) : (
          <div className="sideBarSectionScroll">
            {filteredItems.map(({ path, label, icon }) => {
              const active = location.pathname.startsWith(path);
              const isMessageItem =
                path === RoutesPaths.ADMINMESSAGES ||
                path === RoutesPaths.PARTNERMESSAGES;

              return (
                <NavLink
                  key={path}
                  to={path}
                  className={`sideBarSection ${
                    active ? "sideBarSectionActive" : ""
                  } ${role === "partner" ? "partnerSidebar" : ""}`}
                >
                  <img src={icon} alt={label} />
                  <h6
                    className={`m-0 ${role === "partner" ? "partnerSidebarText" : ""}`}
                  >
                    {label}
                  </h6>
                  {isMessageItem && messageUnreadCount > 0 && (
                    <span className="sideBarUnreadBadge">
                      {messageUnreadCount}
                    </span>
                  )}
                </NavLink>
              );
            })}

            <div
              className={
                role === "partner" ? "sideBarSectionPartner" : "sideBarSection"
              }
              onClick={signOutModel}
            >
              {role === "partner" ? (
                <img src="/assets/img/whiteLogout.svg" alt="Logout" />
              ) : (
                <img src="/assets/img/logout.svg" alt="Logout" />
              )}
              <h6 className="m-0">Logout</h6>
            </div>

            {role !== "partner" && (
              <img
                src="/assets/img/sidebarImage.png"
                alt="Sidebar BG"
                className="img-fluid"
              />
            )}
          </div>
        )}
      </div>

      {/* LOGOUT MODAL */}
      <Modal centered show={IsSignOutModel} onHide={signOutModelClose}>
        <div className="ModelSection">
          <div className="logoutModelContent">
            <div className="logOutIconHeader">
              <div className="logOutIcon">
                <img src="/assets/img/logout-outlined.svg" alt=" " />
              </div>
              <img
                src="/assets/img/close.svg"
                alt=""
                onClick={signOutModelClose}
              />
            </div>
            <div className="deleteContent">
              <h3 className="m-0 deleteTitle">Logout this account</h3>
              <p className="m-0 deleteTitlePera">
                Are you sure you want to logout this account?
              </p>
            </div>
          </div>
          <div className="buttonContent">
            <button
              className="actionButton cancelButton"
              onClick={signOutModelClose}
            >
              Cancel
            </button>
            <button
              className={`actionButton ${role === "admin" ? "logoutButton" : "logoutButtonPartner"}`}
              onClick={signOutSectionClose}
            >
              Logout
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function getRoomsData(response) {
  const data = response?.data?.data ?? response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rooms)) return data.rooms;
  if (Array.isArray(data?.data?.rooms)) return data.data.rooms;

  return [];
}

function getUnreadCount(room) {
  const unreadCount = Number(room?.unreadCount || 0);

  return Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0;
}

function getReadRooms(storageKey) {
  if (typeof window === "undefined") return {};

  try {
    const savedRooms = JSON.parse(localStorage.getItem(storageKey) || "{}");

    return savedRooms && typeof savedRooms === "object" ? savedRooms : {};
  } catch (error) {
    return {};
  }
}

function isRoomRead(room, readRooms) {
  const readAt = readRooms[String(room?._id || "")];

  if (!readAt) return false;

  const readTime = Date.parse(readAt);
  const lastMessageTime = Date.parse(
    room?.lastMessageAt || room?.updatedAt || room?.createdAt || "",
  );

  if (!Number.isFinite(readTime)) return false;
  if (!Number.isFinite(lastMessageTime)) return true;

  return lastMessageTime <= readTime;
}
